import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calculator, TrendingUp, Handshake, Scale, ArrowUpRight, XOctagon } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { SectionHeading } from '../components/SectionHeading';
import { BlogCard } from '../components/Cards';
import { blogs } from '../data/mockData';
import { CTASection } from '../components/CTASection';
import { PageHero } from '../components/PageHero';
export function Kennisbank() {
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const categories = [{
    name: 'Bedrijfswaardering',
    slug: 'bedrijfswaardering',
    icon: <BookOpen size={24} data-id="element-581" />,
    count: 12,
    description: 'De basisprincipes en het proces van waardebepaling.'
  }, {
    name: 'Kosten',
    slug: 'kosten',
    icon: <Calculator size={24} data-id="element-582" />,
    count: 5,
    description: 'Inzicht in de tarieven en opbouw van de kosten.'
  }, {
    name: 'Methodes',
    slug: 'methodes',
    icon: <TrendingUp size={24} data-id="element-583" />,
    count: 8,
    description: 'Uitleg over DCF, multiples en andere methodieken.'
  }, {
    name: 'Verkoop',
    slug: 'verkoop',
    icon: <ArrowUpRight size={24} data-id="element-584" />,
    count: 15,
    description: 'Uw bedrijf voorbereiden op een succesvolle exit.'
  }, {
    name: 'Overname',
    slug: 'overname',
    icon: <Handshake size={24} data-id="element-585" />,
    count: 10,
    description: "Risico's beperken bij de aankoop van een bedrijf."
  }, {
    name: 'Situaties',
    slug: 'situaties',
    icon: <Scale size={24} data-id="element-586" />,
    count: 7,
    description: 'Waardering bij scheiding, conflict of overdracht.'
  }, {
    name: 'Waarde Verhogen',
    slug: 'waarde-verhogen',
    icon: <TrendingUp size={24} data-id="element-587" />,
    count: 9,
    description: 'Strategieën om uw bedrijfswaarde te optimaliseren.'
  }, {
    name: 'Fouten',
    slug: 'fouten',
    icon: <XOctagon size={24} data-id="element-588" />,
    count: 4,
    description: 'Veelvoorkomende valkuilen en hoe u ze vermijdt.'
  }];
  const toggleFilter = (slug: string) => {
    setActiveFilters(prev => prev.includes(slug) ? prev.filter(f => f !== slug) : [...prev, slug]);
  };
  const filteredBlogs = useMemo(() => {
    if (activeFilters.length === 0) return blogs;
    return blogs.filter(blog => activeFilters.includes(blog.cluster));
  }, [activeFilters]);
  return <div className="w-full" data-id="element-589">
      <PageHero title={<>
            <span className="font-medium" data-id="element-591">Kennisbank</span>
          </>} subtitle="Verdiep u in de wereld van bedrijfswaarderingen. Artikelen, gidsen en inzichten geschreven door onze experts." data-id="element-590" />

      <section className="bg-white" data-id="element-592">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pt-16 pb-6" data-id="element-593">
          <AnimatedSection data-id="element-594">
            <h2 className="font-light text-charcoal mb-3 text-[36px]" data-id="element-595">
              Artikelen
            </h2>
            <div className="h-1 w-12 bg-accent mb-4" data-id="element-596" />
            <p className="text-charcoal/50 text-sm font-medium uppercase tracking-wider mb-6" data-id="element-597">
              Filters
            </p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-598">
            {categories.map((cat, index) => {
            const isActive = activeFilters.includes(cat.slug);
            return <AnimatedSection key={index} delay={index * 0.05} data-id="element-599">
                  <button onClick={() => toggleFilter(cat.slug)} className={`flex items-start gap-4 p-6 rounded-2xl border transition-all group h-full w-full text-left ${isActive ? 'border-accent bg-accent/5 shadow-subtle ring-2 ring-accent/20' : 'border-warmgray/50 hover:border-accent hover:shadow-subtle bg-offwhite'}`} data-id="element-600">
                    <div className={`mt-1 transition-colors ${isActive ? 'text-accent' : 'text-charcoal/50 group-hover:text-accent'}`} data-id="element-601">
                      {cat.icon}
                    </div>
                    <div data-id="element-602">
                      <h3 className={`font-medium text-lg mb-1 transition-colors ${isActive ? 'text-accent' : 'text-charcoal'}`} data-id="element-603">
                        {cat.name}
                      </h3>
                      <p className="text-sm text-charcoal/60 mb-3 line-clamp-2" data-id="element-604">
                        {cat.description}
                      </p>
                      <span className="text-xs font-medium text-accent uppercase tracking-wider" data-id="element-605">
                        {cat.count} artikelen
                      </span>
                    </div>
                  </button>
                </AnimatedSection>;
          })}
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pt-8 pb-24" data-id="element-606">
          <AnimatedSection className="mb-8" data-id="element-607">
            <SectionHeading title="Recent" data-id="element-608" />
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-609">
            {filteredBlogs.map((blog, index) => <AnimatedSection key={blog.id} delay={index * 0.1} data-id="element-610">
                <BlogCard {...blog} data-id="element-611" />
              </AnimatedSection>)}
            {filteredBlogs.length === 0 && <div className="col-span-full text-center py-16" data-id="element-612">
                <p className="text-charcoal/50 text-lg" data-id="element-613">
                  Geen artikelen gevonden voor de geselecteerde filters.
                </p>
                <button onClick={() => setActiveFilters([])} className="mt-4 text-accent font-medium hover:underline" data-id="element-614">
                  Wis filters
                </button>
              </div>}
          </div>
        </div>
      </section>

      <CTASection data-id="element-615" />
    </div>;
}